import React, { useEffect, useRef, useState } from 'react';
import { isElectronRuntime } from '../../realm/isRealmEnabled';
import { subscribeDesktopBoot } from '../../utils/desktopBoot';

/**
 * Desktop-only overlay after the native splash closes, while Realm/workspace sync.
 */
const DesktopBootOverlay = () => {
  const [visible, setVisible] = useState(() => isElectronRuntime());
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Loading...');
  const [detail, setDetail] = useState('Starting Workloop');
  const [percent, setPercent] = useState(10);
  const shownAtRef = useRef(Date.now());
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (!isElectronRuntime()) return undefined;

    let cancelled = false;
    const notifyReady = () => {
      try {
        window.workloopRealm?.notifyAppReady?.();
      } catch {
        // ignore
      }
    };

    const hide = () => {
      if (cancelled) return;
      const elapsed = Date.now() - shownAtRef.current;
      const wait = Math.max(0, 420 - elapsed);
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        if (cancelled) return;
        setPercent(100);
        setStatus('Ready');
        setDetail('Opening Workloop');
        window.setTimeout(() => {
          if (!cancelled) setVisible(false);
        }, 180);
      }, wait);
    };

    notifyReady();

    const unsubscribe = subscribeDesktopBoot((payload) => {
      if (cancelled) return;
      if (payload.label) setStatus(payload.label);
      if (payload.detail != null) setDetail(payload.detail);
      if (Number.isFinite(payload.percent)) {
        setPercent((prev) => Math.max(prev, Math.min(100, payload.percent)));
      }
      if (payload.error) {
        setError(String(payload.error));
        setVisible(true);
        return;
      }
      if (payload.done) hide();
    });

    const failTimer = window.setTimeout(() => {
      if (cancelled) return;
      setError('Still syncing… If this stays blank, quit and reopen Workloop.');
      hide();
    }, 22000);

    const onError = (event) => {
      const message = event?.reason?.message || event?.message || 'Unexpected startup error';
      if (cancelled) return;
      setError(String(message));
      setVisible(true);
    };
    window.addEventListener('unhandledrejection', onError);
    window.addEventListener('error', onError);

    return () => {
      cancelled = true;
      unsubscribe();
      window.clearTimeout(hideTimerRef.current);
      window.clearTimeout(failTimer);
      window.removeEventListener('unhandledrejection', onError);
      window.removeEventListener('error', onError);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-[#09090b] text-white"
      role="status"
      aria-live="polite"
    >
      <div className="w-[300px] flex flex-col items-center px-4">
        <p className="m-0 text-[34px] font-bold tracking-tight leading-none">
          <span className="text-[#1cb695]">Work</span>
          <span className="text-[#2f7dff]">Loop</span>
        </p>
        <p className="mt-4 text-[12px] font-semibold tracking-[0.08em] uppercase text-[#8b93a7]">
          {status}
        </p>
        <p className="mt-2 text-[12px] text-[#aeb6c8] text-center">
          {detail}
        </p>
        <div
          className="relative mt-5 w-full h-1.5 overflow-hidden rounded-full bg-[#1a1b22]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percent)}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#1cb695] to-[#2f7dff] shadow-[0_0_14px_rgba(47,125,255,0.35)] transition-[width] duration-500 ease-out"
            style={{ width: `${Math.max(8, percent)}%` }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[desktopBootSheen_1.6s_ease-in-out_infinite]" />
        </div>
        <p className="mt-2 text-[11px] tabular-nums text-[#8b93a7]">
          {Math.round(percent)}%
        </p>
        {error ? (
          <p className="mt-4 w-full rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-left text-[12px] text-rose-200 whitespace-pre-wrap">
            {error}
          </p>
        ) : null}
      </div>
      <style>
        {`@keyframes desktopBootSheen { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}
      </style>
    </div>
  );
};

export default DesktopBootOverlay;
